// Advanced Thermal Printer Utility with ESC/POS Commands
export const printKOTDirect = async (kotData) => {
  try {
    // Check if thermal printer is available
    const hasThermalPrinter = await checkThermalPrinter();
    
    if (!hasThermalPrinter) {
      console.log('Thermal printer not detected, using fallback');
      return false;
    }

    // Create ESC/POS commands for thermal printer
    const escposCommands = generateESCPOSCommands(kotData);
    
    // Send to printer via USB/Bluetooth/WebSocket
    const success = await sendToThermalPrinter(escposCommands);
    
    return success;
  } catch (error) {
    console.error('Thermal printer error:', error);
    return false;
  }
};

// Generate ESC/POS commands for thermal printer
const generateESCPOSCommands = (kotData) => {
  console.log('Thermal printer KOT data:', JSON.stringify(kotData, null, 2));
  
  let commands = [];
  
  // Initialize printer
  commands.push([0x1B, 0x40]); // ESC @ - Initialize printer
  
  // Set text alignment to center
  commands.push([0x1B, 0x61, 0x01]); // ESC a 1 - Center align
  
  // Restaurant name (bold, large)
  commands.push([0x1B, 0x21, 0x30]); // ESC ! 48 - Double height, double width
  commands.push(kotData.restaurantName);
  commands.push([0x0A]); // Line feed
  
  // KOT Header
  commands.push([0x1B, 0x21, 0x00]); // ESC ! 0 - Normal text
  commands.push('*** KOT ***');
  commands.push([0x0A]);
  
  // Separator line
  commands.push('━━━━━━━━━━━━━━━━━━━━');
  commands.push([0x0A, 0x0A]);
  
  // Set alignment to left
  commands.push([0x1B, 0x61, 0x00]); // ESC a 0 - Left align
  
  // Order information
  commands.push([0x1B, 0x21, 0x08]); // ESC ! 8 - Bold
  commands.push(`Order #: ${kotData.orderNumber}`);
  commands.push([0x0A]);
  commands.push(`Table: ${kotData.tableNumber}`);
  commands.push([0x0A]);
  commands.push(`Order Type: ${kotData.orderType ? kotData.orderType.replace('-', ' ').toUpperCase() : 'DINE IN'}`);
  commands.push([0x0A]);
  commands.push(`Customer: ${kotData.customerName}`);
  commands.push([0x0A]);
  commands.push(`Time: ${new Date(kotData.orderTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`);
  commands.push([0x0A]);
  
  // Separator
  commands.push([0x1B, 0x21, 0x00]); // Normal text
  commands.push('━━━━━━━━━━━━━━━━━━━━');
  commands.push([0x0A]);
  
  // Items header
  commands.push([0x1B, 0x21, 0x08]); // Bold
  commands.push('ITEM'.padEnd(25) + 'QTY');
  commands.push([0x0A]);
  commands.push('━━━━━━━━━━━━━━━━━━━━');
  commands.push([0x0A]);
  
  // Items
  commands.push([0x1B, 0x21, 0x00]); // Normal text
  kotData.items.forEach(item => {
    const itemName = item.name.substring(0, 23);
    commands.push(itemName.padEnd(25) + `${item.quantity}x`);
    commands.push([0x0A]);
    
    if (item.customizations && item.customizations.length > 0) {
      const notes = `Note: ${Array.isArray(item.customizations) ? item.customizations.join(', ') : item.customizations}`;
      commands.push(notes.substring(0, 35));
      commands.push([0x0A]);
    }
    console.log(item.bogoApplied,"item.bogoApplied")
    // Add BOGO offer section if applied
    if ((item.bogoApplied === true || item.bogoApplied === 'true') && item.bogoDetails) {
      console.log('BOGO detected in thermal printer:', item.bogoApplied, item.bogoDetails);
      commands.push([0x0A]);
      commands.push([0x1B, 0x21, 0x08]); // Bold
      commands.push(`Offer Applied: ${item.bogoDetails.offerText || 'Buy 1 Get 1 Free'}`);
      commands.push([0x0A]);
      commands.push([0x1B, 0x21, 0x00]); // Normal text
      
      if (item.freeItems && item.freeItems.length > 0) {
        commands.push('Free Items:');
        commands.push([0x0A]);
        item.freeItems.forEach(freeItem => {
          const freeItemName = freeItem.name.substring(0, 20);
          commands.push(`  ${freeItemName.padEnd(20)} ${freeItem.quantity}x`);
          commands.push([0x0A]);
        });
      }
      commands.push('━━━━━━━━━━━━━━━━━━━━');
      commands.push([0x0A]);
    } else {
      console.log('BOGO not detected:', item.bogoApplied, item.bogoDetails);
    }
  });
  
  // Special instructions
  if (kotData.specialInstructions) {
    commands.push([0x0A]);
    commands.push('━━━━━━━━━━━━━━━━━━━━');
    commands.push([0x0A]);
    commands.push([0x1B, 0x21, 0x08]); // Bold
    commands.push('⚠ SPECIAL INSTRUCTIONS:');
    commands.push([0x0A]);
    commands.push([0x1B, 0x21, 0x00]); // Normal text
    commands.push(kotData.specialInstructions);
    commands.push([0x0A]);
  }
  
  // Footer
  commands.push([0x0A]);
  commands.push('━━━━━━━━━━━━━━━━━━━━');
  commands.push([0x0A]);
  commands.push([0x1B, 0x61, 0x01]); // Center align
  commands.push([0x1B, 0x21, 0x08]); // Bold
  commands.push(kotData.orderType ? kotData.orderType.replace('-', ' ').toUpperCase() : 'DINE IN');
  commands.push([0x0A, 0x0A, 0x0A]); // Extra paper for cutting
  
  // Cut paper
  commands.push([0x1B, 0x69]); // ESC i - Cut
  
  return commands;
};

// Send commands to thermal printer
const sendToThermalPrinter = async (commands) => {
  try {
    // Method 1: WebSocket (for network printers)
    if (window.WebSocket) {
      const ws = new WebSocket('ws://localhost:9100'); // Default thermal printer port
      ws.onopen = () => {
        commands.forEach(cmd => {
          if (typeof cmd === 'string') {
            ws.send(cmd);
          } else if (Array.isArray(cmd)) {
            ws.send(new Uint8Array(cmd));
          }
        });
        ws.close();
      };
      return true;
    }
    
    // Method 2: USB/WebUSB (if supported)
    if (navigator.usb) {
      try {
        const device = await navigator.usb.requestDevice({
          filters: [{ vendorId: 0x0483 }] // Example vendor ID for thermal printers
        });
        await device.open();
        await device.claimInterface(0);
        
        for (const cmd of commands) {
          const data = typeof cmd === 'string' 
            ? new TextEncoder().encode(cmd)
            : new Uint8Array(cmd);
          await device.transferOut(1, data);
        }
        
        await device.close();
        return true;
      } catch (usbError) {
        console.log('USB printer not available');
      }
    }
    
    // Method 3: Print window fallback
    return false;
  } catch (error) {
    console.error('Failed to send to thermal printer:', error);
    return false;
  }
};

// Check if thermal printer is available
export const checkThermalPrinter = () => {
  return new Promise((resolve) => {
    // Check for various printer APIs
    const hasWebUSB = navigator.usb !== undefined;
    const hasWebSocket = window.WebSocket !== undefined;
    
    // Try to detect printer via WebSocket
    if (hasWebSocket) {
      const ws = new WebSocket('ws://localhost:9100');
      ws.onopen = () => {
        ws.close();
        resolve(true);
      };
      ws.onerror = () => {
        resolve(hasWebUSB); // Fallback to WebUSB check
      };
      setTimeout(() => {
        resolve(false);
      }, 1000);
    } else {
      resolve(hasWebUSB);
    }
  });
};

// Alternative: Simple text-based thermal print
export const printKOTSimple = async (kotData) => {
  try {
    // Create formatted text for thermal printer
    let kotText = '';
    
    // Configure for 58mm thermal printer (32 characters per line)
    const lineWidth = 32;
    
    // Center function
    const center = (text) => {
      const padding = Math.floor((lineWidth - text.length) / 2);
      return ' '.repeat(Math.max(0, padding)) + text;
    };
    
    // Header
    kotText += center('*** KOT ***') + '\n';
    kotText += center(kotData.restaurantName.substring(0, lineWidth)) + '\n';
    kotText += '━━━━━━━━━━━━━━━━━━━━\n\n';
    
    // Order Info
    kotText += `Order #: ${kotData.orderNumber}\n`;
    kotText += `Table: ${kotData.tableNumber}\n`;
    kotText += `Order Type: ${kotData.orderType ? kotData.orderType.replace('-', ' ').toUpperCase() : 'DINE IN'}\n`;
    kotText += `Customer: ${kotData.customerName}\n`;
    kotText += `Time: ${new Date(kotData.orderTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}\n`;
    kotText += '━━━━━━━━━━━━━━━━━━━━\n\n';
    
    // Items
    kotText += center('ITEM QTY') + '\n';
    kotText += '━━━━━━━━━━━━━━━━━━━━\n';
    
    kotData.items.forEach(item => {
      const maxItemNameLength = lineWidth - 8; // Reserve space for quantity
      let itemName = item.name.substring(0, maxItemNameLength);
      const qtyStr = `${item.quantity}x`;
      
      // Format: Item name (left aligned) + quantity (right aligned)
      const spacing = lineWidth - itemName.length - qtyStr.length;
      kotText += itemName + ' '.repeat(Math.max(0, spacing)) + qtyStr + '\n';
      
      if (item.customizations && item.customizations.length > 0) {
        const notes = `Note: ${Array.isArray(item.customizations) ? item.customizations.join(', ') : item.customizations}`;
        // Wrap long notes
        const wrappedNotes = notes.match(/.{1,30}/g) || [notes];
        wrappedNotes.forEach(note => {
          kotText += '  ' + note + '\n';
        });
      }
      
      // Add BOGO offer section if applied
      if ((item.bogoApplied === true || item.bogoApplied === 'true') && item.bogoDetails) {
        console.log('BOGO detected in simple thermal printer:', item.bogoApplied, item.bogoDetails);
        kotText += '\n';
        kotText += 'Offer Applied: ' + (item.bogoDetails.offerText || 'Buy 1 Get 1 Free') + '\n';
        
        if (item.freeItems && item.freeItems.length > 0) {
          kotText += 'Free Items:\n';
          item.freeItems.forEach(freeItem => {
            const freeItemName = freeItem.name.substring(0, lineWidth - 6);
            const freeQtyStr = `${freeItem.quantity}x`;
            const freeSpacing = lineWidth - 2 - freeItemName.length - freeQtyStr.length; // 2 spaces for indent
            kotText += '  ' + freeItemName + ' '.repeat(Math.max(0, freeSpacing)) + freeQtyStr + '\n';
          });
        }
        kotText += '━━━━━━━━━━━━━━━━━━━━\n';
      } else {
        console.log('BOGO not detected in simple thermal printer:', item.bogoApplied, item.bogoDetails);
      }
    });
    
    // Special Instructions
    if (kotData.specialInstructions) {
      kotText += '\n━━━━━━━━━━━━━━━━━━━━\n';
      kotText += '⚠ SPECIAL INSTRUCTIONS:\n';
      // Wrap long instructions
      const wrappedInstructions = kotData.specialInstructions.match(/.{1,30}/g) || [kotData.specialInstructions];
      wrappedInstructions.forEach(instruction => {
        kotText += instruction + '\n';
      });
    }
    
    // Footer
    kotText += '\n━━━━━━━━━━━━━━━━━━━━\n';
    kotText += center(kotData.orderType ? kotData.orderType.replace('-', ' ').toUpperCase() : 'DINE IN') + '\n';
    kotText += '\n\n\n'; // Extra paper for cutting
    
    // Create blob and print
    const blob = new Blob([kotText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const printWindow = window.open(url, '_blank', 'width=400,height=600');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
          URL.revokeObjectURL(url);
        }, 1000);
      };
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Simple thermal printer error:', error);
    return false;
  }
};
